import { GoogleGenerativeAI } from '@google/generative-ai';
import { ChecklistItem } from '@/firebase/checklistService';
import type { ExtractedEvidenceFields } from '@/services/autoCompleteChecklistService';

// ============================================================================
// VERTEX AI MIGRATION - Enterprise Gemini AI for Bank Compliance
// ============================================================================
// Migrating from public Gemini API to Vertex AI for enterprise security
// All data is guaranteed NOT to be used for training (enterprise data governance)

// Flag to enable Vertex AI (set to true to use Vertex AI, false for legacy Gemini API)
const USE_VERTEX_AI = true; // TODO: Set to true after testing

// Get Firebase Functions URL for Vertex AI proxy
const getVertexAIProxyUrl = (): string => {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'lemur-476302';
  const isLocalhost = typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.')
  );
  
  if (isLocalhost) {
    return `http://localhost:5001/${projectId}/us-central1/callVertexAI`;
  }
  
  return `https://us-central1-${projectId}.cloudfunctions.net/callVertexAI`;
};

/**
 * Call Vertex AI through Firebase Functions proxy
 * This ensures enterprise security and data governance
 */
async function callVertexAIProxy(
  prompt: string | string[],
  fileData?: { data: string; mimeType: string },
  returnJSON: boolean = false
): Promise<string | any> {
  try {
    const url = getVertexAIProxyUrl();
    console.log('[VertexAI Proxy] Calling:', {
      url,
      promptType: Array.isArray(prompt) ? 'array' : typeof prompt,
      promptLength: Array.isArray(prompt) ? prompt.length : (typeof prompt === 'string' ? prompt.length : 0),
      hasFileData: !!fileData,
      fileMimeType: fileData?.mimeType,
      returnJSON,
    });

    const requestBody = {
      operation: 'generateContent',
      prompt: Array.isArray(prompt) ? prompt : [prompt],
      fileData,
      returnJSON,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    console.log('[VertexAI Proxy] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      console.error('[VertexAI Proxy] HTTP Error:', errorData);
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('[VertexAI Proxy] Response received:', {
      success: result.success,
      hasData: !!result.data,
      dataType: typeof result.data,
    });

    if (!result.success) {
      console.error('[VertexAI Proxy] API Error:', result.error);
      throw new Error(result.error || 'Vertex AI call failed');
    }

    return result.data;
  } catch (error: any) {
    console.error('[VertexAI Proxy] Error:', {
      message: error.message,
      stack: error.stack,
      name: error.name,
    });
    throw new Error(`Vertex AI proxy error: ${error.message || 'Unknown error'}`);
  }
}

// Legacy Gemini AI initialization (kept for fallback)
// Note: API key is hardcoded here due to .env file encoding issues on Windows
const apiKey = import.meta.env.VITE_GEMINI_API_KEY || 'AIzaSyBaICKtyHUuxw1tFATAUYbzSQjqzEtOp-I';

const genAI = apiKey ? new GoogleGenerativeAI(apiKey) : null;

export interface DocumentAnalysis {
  summary: string;
  expiryDate?: Date | null;
  extractedDates: { type: string; date: Date; confidence: string }[];
  documentType?: string;
  keyInformation: Record<string, string>;
}

export interface ChecklistExtractionItem {
  order?: number;
  text: string;
  notes?: string;
  evidenceDefinitions?: {
    evidenceName: string;
    required: boolean;
    description?: string;
    fieldDefinitions?: {
      key: string;
      label: string;
      type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list';
      required: boolean;
      enumValues?: string[];
    }[];
  }[];
}

export interface ChecklistExtractionResult {
  name: string;
  description?: string;
  items: ChecklistExtractionItem[];
  variables?: string[];
  confidenceNotes?: string;
}

export interface TableFormItemsExtractionResult {
  items: string[];
  confidenceNotes?: string;
}

export interface FieldExtractionResult {
  fieldValues: Record<string, any>;
  confidence: 'high' | 'medium' | 'low';
  notes?: string;
  serviceUsed?: 'VERTEX_AI' | 'PUBLIC_GEMINI'; // Track which service was used
  timestamp?: string; // When extraction occurred
}

export interface FactsExtractionResult {
  facts: Record<string, any>; // factKey -> value
  confidence: 'high' | 'medium' | 'low';
  notes?: string;
}

/**
 * Convert file to base64 for Gemini API
 */
async function fileToBase64(file: File): Promise<{ mimeType: string; data: string }> {
  return new Promise<{ mimeType: string; data: string }>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1];
      resolve({
        mimeType: file.type,
        data: base64,
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Parse a document using Gemini Vision API
 */
export async function parseDocument(file: File): Promise<DocumentAnalysis> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    // Convert file to base64
    const fileBase64 = await fileToBase64(file);
    
    // Determine if it's an image or PDF
    const isImage = file.type.startsWith('image/');
    const isPDF = file.type === 'application/pdf';

    if (!isImage && !isPDF) {
      throw new Error('Unsupported file type. Please upload PDF or image files.');
    }

    const prompt = `Analyze this document and provide:
1. A concise summary (2-3 sentences)
2. Identify if this is a business license, permit, certificate, or other document type
3. Extract ALL dates found in the document, especially:
   - Expiry/expiration dates
   - Issue dates
   - Renewal dates
   - Effective dates
4. For each date, identify its type (expiry, issue, renewal, etc.) and confidence level (high, medium, low)
5. Extract key information as key-value pairs (license number, business name, etc.)

Please respond in JSON format:
{
  "summary": "Brief summary of the document",
  "documentType": "business license | permit | certificate | other",
  "expiryDate": "YYYY-MM-DD or null if not found",
  "extractedDates": [
    {
      "type": "expiry | issue | renewal | effective | other",
      "date": "YYYY-MM-DD",
      "confidence": "high | medium | low"
    }
  ],
  "keyInformation": {
    "licenseNumber": "if found",
    "businessName": "if found",
    "otherKeyFields": "values"
  }
}`;

    let parsedResult: any;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[parseDocument] ✅ Using Vertex AI');
      const fileData = {
          data: fileBase64.data,
          mimeType: isPDF ? 'application/pdf' : fileBase64.mimeType,
      };
      parsedResult = await callVertexAIProxy(prompt, fileData, true);
      // Result is already parsed JSON object
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[parseDocument] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    // Process the parsed result (parseGeminiResponse expects string, so convert if needed)
    if (typeof parsedResult === 'string') {
      return parseGeminiResponse(parsedResult);
    } else {
      // Already parsed, process directly
      const expiryDate = parsedResult.expiryDate ? new Date(parsedResult.expiryDate) : null;
      const extractedDates = (parsedResult.extractedDates || []).map((d: any) => ({
        type: d.type || 'other',
        date: new Date(d.date),
        confidence: d.confidence || 'medium',
      }));

      return {
        summary: parsedResult.summary || 'No summary available',
        expiryDate: expiryDate && !isNaN(expiryDate.getTime()) ? expiryDate : null,
        extractedDates,
        documentType: parsedResult.documentType || 'other',
        keyInformation: parsedResult.keyInformation || {},
      };
    }
  } catch (error: any) {
    console.error('Error parsing document with Gemini:', error);
    throw new Error(`Failed to parse document: ${error.message}`);
  }
}

/**
 * Parse Gemini's JSON response
 */
function parseGeminiResponse(text: string): DocumentAnalysis {
  try {
    // Extract JSON from the response (sometimes it's wrapped in markdown code blocks)
    let jsonText = text.trim();
    
    // Remove markdown code blocks if present
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.replace(/^```json\n/, '').replace(/\n```$/, '');
    } else if (jsonText.startsWith('```')) {
      jsonText = jsonText.replace(/^```\n/, '').replace(/\n```$/, '');
    }
    
    const parsed = JSON.parse(jsonText);
    
    // Convert string dates to Date objects
    const expiryDate = parsed.expiryDate ? new Date(parsed.expiryDate) : null;
    const extractedDates = (parsed.extractedDates || []).map((d: any) => ({
      type: d.type || 'other',
      date: new Date(d.date),
      confidence: d.confidence || 'medium',
    }));

    return {
      summary: parsed.summary || 'No summary available',
      expiryDate: expiryDate && !isNaN(expiryDate.getTime()) ? expiryDate : null,
      extractedDates,
      documentType: parsed.documentType || 'other',
      keyInformation: parsed.keyInformation || {},
    };
  } catch (error) {
    console.error('Error parsing Gemini response:', error);
    // Return a fallback response
    return {
      summary: text.substring(0, 500), // Use raw text as summary if JSON parsing fails
      expiryDate: null,
      extractedDates: [],
      documentType: 'other',
      keyInformation: {},
    };
  }
}

/**
 * Calculate new due date based on expiry date
 * Returns a date a few days before expiry as the new due date
 */
export function calculateDueDateFromExpiry(expiryDate: Date, leadTimeDays: number = 30): Date {
  const dueDate = new Date(expiryDate);
  dueDate.setDate(dueDate.getDate() - leadTimeDays);
  return dueDate;
}

/**
 * Analyze form completion data and generate a concise summary
 */
export async function analyzeFormCompletion(
  formName: string,
  formFields: Array<{ label: string; type: string }>,
  fieldValues: Record<string, any>
): Promise<string> {
    // Build a structured representation of the form data
    const formDataSummary = Object.entries(fieldValues)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([label, value]) => {
        const field = formFields.find(f => f.label === label);
        return `${label}: ${String(value)}`;
      })
      .join('\n');

    if (!formDataSummary || formDataSummary.trim().length === 0) {
      console.warn('⚠️ No form data to summarize, returning fallback');
      return 'Form submission with minimal data.';
    }

    const prompt = `Analyze this form completion data and provide a concise, informative summary (2-3 sentences maximum).

Form Name: ${formName}

Form Data:
${formDataSummary}

Please provide a brief summary highlighting:
- Key information from the form
- Any notable details or important values
- Context that would be useful for someone reviewing this form submission

Keep it concise, factual, and informative. Do not include generic phrases or filler text.`;

  if (!USE_VERTEX_AI && !genAI) {
    const errorMsg = 'Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.';
    console.error('❌', errorMsg);
    console.warn('⚠️ Using fallback summary instead of AI-generated summary');
    // Return fallback instead of throwing
    const nonEmptyFields = Object.entries(fieldValues)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .slice(0, 3)
      .map(([label]) => label)
      .join(', ');
    return nonEmptyFields 
      ? `Form submission with ${Object.keys(fieldValues).filter(k => fieldValues[k] !== null && fieldValues[k] !== undefined && fieldValues[k] !== '').length} completed fields including ${nonEmptyFields}.`
      : 'Form submission with minimal data.';
  }

  try {
    let text: string;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[analyzeFormCompletion] ✅ Using Vertex AI');
      const result = await callVertexAIProxy(prompt, undefined, false);
      text = typeof result === 'string' ? result : JSON.stringify(result);
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[analyzeFormCompletion] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    if (!text || text.length === 0) {
      console.warn('⚠️ Vertex AI returned empty response, using fallback');
      throw new Error('Empty response from Vertex AI');
    }
    
    // Clean up any markdown formatting if present
    const cleanedText = text.replace(/^```\w*\n?/, '').replace(/\n?```$/, '').trim();
    console.log('✅ AI summary generated successfully:', cleanedText.substring(0, 100) + '...');
    return cleanedText;
  } catch (error: any) {
    console.error('❌ Error analyzing form completion with Gemini:', error);
    console.error('Error details:', {
      message: error?.message,
      code: error?.code,
      status: error?.status,
      statusText: error?.statusText,
      name: error?.name
    });
    
    // Return a fallback summary instead of throwing
    console.log('🔄 Generating fallback summary...');
    const nonEmptyFields = Object.entries(fieldValues)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .slice(0, 3)
      .map(([label]) => label)
      .join(', ');
    
    const fallbackSummary = nonEmptyFields 
      ? `Form submission with ${Object.keys(fieldValues).filter(k => fieldValues[k] !== null && fieldValues[k] !== undefined && fieldValues[k] !== '').length} completed fields including ${nonEmptyFields}.`
      : 'Form submission with minimal data.';
    
    console.log('✅ Fallback summary generated:', fallbackSummary);
    return fallbackSummary;
  }
}

/**
 * Parse a contract document using Gemini Vision API
 * Extracts summary, contract date, expiry date, and key information
 */
export async function parseContract(file: File): Promise<ContractAnalysis> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    // Convert file to base64
    const fileBase64 = await fileToBase64(file);
    
    // Determine if it's a PDF or Word document
    const isPDF = file.type === 'application/pdf';
    const isWord = file.type === 'application/msword' || 
                   file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                   file.name.toLowerCase().endsWith('.doc') ||
                   file.name.toLowerCase().endsWith('.docx');

    if (!isPDF && !isWord) {
      throw new Error('Unsupported file type. Please upload PDF or Word documents.');
    }

    const prompt = `Analyze this contract/agreement document and extract the following information:

1. **Document Summary**: Provide a concise 2-3 sentence summary of the contract, including:
   - What type of contract/agreement this is (e.g., service agreement, vendor contract, employment contract, lease agreement, etc.)
   - Main parties involved
   - Primary purpose or scope of the agreement

2. **Contract Date**: Identify the date the contract was signed/executed (look for "Date:", "Effective Date:", "Signed on:", "Execution Date:", etc.)

3. **Expiry Date**: Identify the expiration or end date of the contract (look for "Expiry Date:", "Expiration Date:", "End Date:", "Term Ends:", "Valid Until:", etc.)

4. **Key Information**: Extract important details as key-value pairs, including but not limited to:
   - Contract/Agreement number or ID
   - Parties involved (names, companies)
   - Contract value/amount (if applicable)
   - Payment terms (if applicable)
   - Renewal terms (if applicable)
   - Termination clauses
   - Any other critical information

Please respond in JSON format:
{
  "summary": "Brief summary of the contract (2-3 sentences)",
  "contractDate": "YYYY-MM-DD or null if not found",
  "expiryDate": "YYYY-MM-DD or null if not found",
  "keyInformation": {
    "contractNumber": "if found",
    "party1": "if found",
    "party2": "if found",
    "contractValue": "if found",
    "paymentTerms": "if found",
    "renewalTerms": "if found",
    "terminationClause": "if found",
    "otherKeyFields": "values"
  }
}`;

    let parsedResult: any;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[parseContract] ✅ Using Vertex AI');
      if (isWord) {
        const docxText = await extractTextFromDocx(file);
        parsedResult = await callVertexAIProxy([prompt, docxText], undefined, true);
      } else {
        const fileData = {
          data: fileBase64.data,
          mimeType: 'application/pdf',
        };
        parsedResult = await callVertexAIProxy(prompt, fileData, true);
      }
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[parseContract] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    // Process the parsed result
    if (typeof parsedResult === 'string') {
      return parseContractResponse(parsedResult);
    } else {
      // Already parsed, process directly
      return parseContractResponse(JSON.stringify(parsedResult));
    }
  } catch (error: any) {
    console.error('Error parsing contract with Gemini:', error);
    throw new Error(`Failed to parse contract: ${error.message}`);
  }
}

/**
 * Extract text from DOCX file using mammoth library
 * Note: Only supports .docx files, not older .doc files
 */
async function extractTextFromDocx(file: File): Promise<string> {
  // Check if it's actually a .docx file (mammoth doesn't support .doc)
  const isDocx = file.name.toLowerCase().endsWith('.docx') || 
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  
  if (!isDocx) {
    throw new Error('Only .docx files are supported. Please convert .doc files to .docx format.');
  }

  try {
    // Dynamically import mammoth to avoid loading it if not needed
    const mammoth = await import('mammoth');
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    return result.value;
  } catch (error: any) {
    console.error('Error extracting text from DOCX:', error);
    throw new Error(`Failed to extract text from Word document: ${error.message}. Please ensure the file is a valid .docx file.`);
  }
}

export async function extractChecklistFromDocument(file: File): Promise<ChecklistExtractionResult> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    const mimeType = file.type;
    const isPDF = mimeType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isWord = mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.toLowerCase().endsWith('.doc') ||
      file.name.toLowerCase().endsWith('.docx');
    const isImage = mimeType?.startsWith('image/');

    if (!isPDF && !isWord && !isImage) {
      throw new Error('Unsupported file type. Please upload PDF, Word, or image files.');
    }

    const prompt = `You are an expert compliance assistant. A user uploads a checklist document (PDF, Word, or scanned image) and needs a structured checklist they can edit.

Please analyze the document and convert it into structured JSON with the following schema:
{
  "name": "Concise checklist title (<=80 characters)",
  "description": "Optional one-sentence summary of what this checklist covers",
  "items": [
    {
      "order": 1,
      "text": "Plain-language checklist step (CLEAN TEXT ONLY - no evidence or field info here)",
      "notes": "Optional clarifying detail or requirement",
      "evidenceDefinitions": [
        {
          "evidenceName": "Evidence name (e.g., Joint Intent, Flood Determination)",
          "required": true,
          "description": "Optional description of this evidence requirement",
          "fieldDefinitions": [
            {
              "key": "fieldKey (e.g., borrowerNames, signDate)",
              "label": "Human-readable label (e.g., Borrower Names, Sign Date)",
              "type": "date | string | enum | number | boolean",
              "required": true,
              "enumValues": ["value1", "value2"] // Only if type is "enum"
            }
          ]
        }
      ]
    }
  ],
  "variables": ["Optional list of variable placeholders such as Room Number, Inspector Name"],
  "confidenceNotes": "Optional notes about assumptions, missing info, or sections that could not be read clearly"
}

CRITICAL EXTRACTION RULES:
1. **Item Text**: Extract ONLY the clean, actionable step text. Remove any evidence references, field names, or metadata from the text field.
   - BAD: "Verify business purpose and joint intent. — Evidence: JOINT_INTENT (required). Capture borrowerNames, isBusinessPurposeCertified..."
   - GOOD: "Verify business purpose and joint intent"

2. **Evidence Definitions**: If the document mentions evidence requirements (e.g., "Evidence: JOINT_INTENT", "Requires: FLOOD_DETERMINATION"), extract them into the evidenceDefinitions array.
   - Look for patterns like: "Evidence: [CATEGORY]", "Requires: [CATEGORY]", "[CATEGORY] (required/optional)"
   - Common category codes: JOINT_INTENT, FLOOD_DETERMINATION, FLOOD_INSURANCE_POLICY, FLOOD_NOTICE_SENT, ECOA_RTR_NOTICE_SENT, APPRAISAL_DELIVERED, APPRAISAL_REPORT, HMDA_DATA_CAPTURE, etc.

3. **Field Definitions**: If the document lists fields to capture (e.g., "Capture borrowerNames, isBusinessPurposeCertified, jointIntentConfirmed, signDate, signerNames"), extract them into fieldDefinitions array.
   - Field keys should be camelCase (e.g., "borrowerNames", "signDate", "isBusinessPurposeCertified")
   - Infer field types: dates → "date", yes/no → "boolean", lists → "enum", numbers → "number", text → "string"
   - Labels should be human-readable (e.g., "Borrower Names", "Sign Date", "Is Business Purpose Certified")

4. **General Guidelines**:
   - Preserve the order of steps exactly as the source document.
   - Each step must be actionable and stand on its own.
   - If the document uses sections or numbering (e.g., 1.1, 1.2), flatten them into a single ordered list but keep section context in 'notes' when useful.
   - Avoid duplicate/blank steps. Remove checkboxes or filler text.
   - Prefer 5-40 items. If the document contains descriptive paragraphs, summarize them into a few concise steps.
   - Include variables only when the document explicitly expects a value to be filled in (e.g., Room #, Temperature). Do not invent variables.
   - If the document quality is poor or information is missing, mention it in "confidenceNotes".

Return ONLY valid JSON conforming to the schema.`;

    let parsed: any;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[extractChecklistFromDocument] ✅ Using Vertex AI');
    if (isWord) {
      const docxText = await extractTextFromDocx(file);
        parsed = await callVertexAIProxy([prompt, docxText], undefined, true);
    } else {
      const fileBase64 = await fileToBase64(file);
        const fileData = {
            data: fileBase64.data,
            mimeType: isPDF ? 'application/pdf' : fileBase64.mimeType,
        };
        parsed = await callVertexAIProxy(prompt, fileData, true);
      }
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[extractChecklistFromDocument] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    // Process the parsed result
    const text = typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
    const parsedResult = parseChecklistExtractionResponse(text);

    const fallbackName = file.name.replace(/\.(pdf|docx?|png|jpe?g)$/i, '').replace(/[_-]+/g, ' ').trim() || 'Imported Checklist';
    const normalizedItems = (parsedResult.items || [])
      .filter(item => item && typeof item.text === 'string' && item.text.trim().length > 0)
      .map((item, index) => {
        // Clean the text - remove evidence and field references that might have been included
        let cleanText = item.text.trim();
        let extractedEvidence: any[] = [];
        let extractedFields: any[] = [];
        
        // Try to extract evidence info from text if AI included it
        const evidenceMatch = cleanText.match(/[—\-]\s*Evidence:\s*([A-Z_]+)\s*(\(required\)|\(optional\))?/i);
        if (evidenceMatch) {
          const evidenceName = evidenceMatch[1]; // Keep original case, convert underscores to spaces
          const isRequired = evidenceMatch[2]?.toLowerCase().includes('required') ?? true;
          extractedEvidence.push({
            evidenceName: evidenceName.replace(/_/g, ' '),
            required: isRequired,
          });
        }
        
        // Try to extract field names from "Capture ..." pattern
        const captureMatch = cleanText.match(/Capture\s+([^.]*)/i);
        if (captureMatch) {
          const fieldNames = captureMatch[1].split(',').map(f => f.trim()).filter(f => f);
          extractedFields = fieldNames.map(fieldName => {
            // Convert to camelCase key
            const key = fieldName
              .toLowerCase()
              .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
              .replace(/[^a-z0-9]/gi, '');
            
            // Infer type from field name
            let type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list' = 'string';
            if (fieldName.toLowerCase().includes('date')) type = 'date';
            else if (fieldName.toLowerCase().includes('is') || fieldName.toLowerCase().includes('confirmed')) type = 'boolean';
            else if (fieldName.toLowerCase().includes('number') || fieldName.toLowerCase().includes('count')) type = 'number';
            
            return {
              key,
              label: fieldName,
              type,
              required: false,
            };
          });
        }
        
        // Remove evidence and field references from text
        cleanText = cleanText.replace(/\s*[—\-]\s*Evidence:.*$/i, '');
        cleanText = cleanText.replace(/\s*Evidence:.*$/i, '');
        cleanText = cleanText.replace(/\s*Capture\s+[^.]*\.?$/i, '');
        cleanText = cleanText.replace(/\s*\(required\)\.?\s*Capture.*$/i, '');
        cleanText = cleanText.replace(/\s*\(optional\)\.?\s*Capture.*$/i, '');
        cleanText = cleanText.trim();

        // Merge extracted evidence with AI-provided evidence
        const evidenceDefinitions = Array.isArray(item.evidenceDefinitions) && item.evidenceDefinitions.length > 0
          ? item.evidenceDefinitions
          : extractedEvidence.length > 0
          ? extractedEvidence
          : undefined;

        return {
          order: typeof item.order === 'number' && item.order > 0 ? item.order : index + 1,
          text: cleanText,
          notes: item.notes?.trim() || undefined,
          evidenceDefinitions: evidenceDefinitions
            ? evidenceDefinitions
                .filter(ev => ev && (ev.evidenceName || (ev as any).categoryCode) && typeof (ev.evidenceName || (ev as any).categoryCode) === 'string')
                .map((ev, evIndex) => ({
                  evidenceName: (ev.evidenceName || (ev as any).categoryCode || '').trim(),
                  required: ev.required === true,
                  description: ev.description?.trim() || undefined,
                  fieldDefinitions: (Array.isArray(ev.fieldDefinitions) && ev.fieldDefinitions.length > 0
                    ? ev.fieldDefinitions
                    : evIndex === 0 && extractedFields.length > 0
                    ? extractedFields
                    : undefined)
                    ?.filter((field: any) => field && field.key && field.label && field.type)
                    .map((field: any) => ({
                      key: field.key.trim(),
                      label: field.label.trim(),
                      type: ['date', 'string', 'enum', 'number', 'boolean', 'list'].includes(field.type) 
                        ? field.type as 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list'
                        : 'string',
                      required: field.required === true,
                      enumValues: Array.isArray(field.enumValues) 
                        ? field.enumValues.filter((v: any) => typeof v === 'string').map((v: any) => v.trim())
                        : undefined,
                    })),
                }))
            : undefined,
        };
      });

    if (normalizedItems.length === 0) {
      throw new Error('No checklist items detected in the uploaded document. Please verify the file content.');
    }

    const variables = Array.isArray(parsedResult.variables)
      ? parsedResult.variables
        .filter((value) => typeof value === 'string' && value.trim().length > 0)
        .map((value) => value.trim())
      : [];

    return {
      name: parsedResult.name?.trim() || fallbackName,
      description: parsedResult.description?.trim() || undefined,
      items: normalizedItems,
      variables: variables.length > 0 ? variables : undefined,
      confidenceNotes: parsedResult.confidenceNotes?.trim() || undefined,
    };
  } catch (error: any) {
    console.error('Error extracting checklist from document:', error);
    throw new Error(error?.message || 'Failed to analyze checklist document.');
  }
}

/**
 * Extract table form items (item names) from an uploaded document
 * This is useful for creating table forms where items are rows (e.g., inventory lists, product lists)
 */
export async function extractTableFormItems(file: File): Promise<TableFormItemsExtractionResult> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    const mimeType = file.type;
    const isPDF = mimeType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isWord = mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.toLowerCase().endsWith('.doc') ||
      file.name.toLowerCase().endsWith('.docx');
    const isImage = mimeType?.startsWith('image/');

    if (!isPDF && !isWord && !isImage) {
      throw new Error('Unsupported file type. Please upload PDF, Word, or image files.');
    }

    const prompt = `You are an expert assistant helping to extract a list of items from a document. The user wants to create a table form where each item will be a row.

Analyze this document and extract ALL item names. Items could be:
- Product names (e.g., "Wine", "Liquor", "Beer")
- Equipment names (e.g., "Fire Extinguisher A", "Smoke Detector B")
- Inventory items (e.g., "Item 1", "Item 2")
- Any list of distinct items/names that should become rows in a table

CRITICAL: Return ONLY valid JSON. No markdown, no explanations, no code blocks. Just pure JSON.

Return the data in this exact JSON format:
{
  "items": ["Item 1", "Item 2", "Item 3"],
  "confidenceNotes": "Optional notes about extraction quality or assumptions"
}

Guidelines:
- Extract ALL unique item names from the document
- If the document contains a table/list, extract the item names from the first column or the most relevant column
- Remove duplicates
- Keep item names concise (prefer short names like "Wine" over "Premium Wine Bottle")
- If items have numbers or codes, include them (e.g., "Item #123" or "Product A-456")
- If the document quality is poor or information is unclear, mention it in "confidenceNotes"
- Return an empty array if no items can be identified
- Maximum 200 items (if more exist, take the first 200)

Return ONLY valid JSON conforming to the schema.`;

    let parsed: { items?: string[]; confidenceNotes?: string };
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[extractTableFormItems] ✅ Using Vertex AI');
    if (isWord) {
      const docxText = await extractTextFromDocx(file);
        parsed = await callVertexAIProxy([prompt, docxText], undefined, true) as { items?: string[]; confidenceNotes?: string };
    } else {
      const fileBase64 = await fileToBase64(file);
        const fileData = {
            data: fileBase64.data,
            mimeType: isPDF ? 'application/pdf' : fileBase64.mimeType,
        };
        parsed = await callVertexAIProxy(prompt, fileData, true) as { items?: string[]; confidenceNotes?: string };
      }
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[extractTableFormItems] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }

    // Normalize items
    const items = Array.isArray(parsed.items)
      ? parsed.items
          .filter((item) => typeof item === 'string' && item.trim().length > 0)
          .map((item) => item.trim())
          .filter((item, index, self) => self.indexOf(item) === index) // Remove duplicates
      : [];

    return {
      items,
      confidenceNotes: parsed.confidenceNotes?.trim() || undefined,
    };
  } catch (error: any) {
    console.error('Error extracting table form items from document:', error);
    throw new Error(error?.message || 'Failed to extract items from document.');
  }
}

/**
 * Contract analysis result
 */
export interface ContractAnalysis {
  summary: string;
  contractDate?: Date | null;
  expiryDate?: Date | null;
  keyInformation: Record<string, string>;
}

/**
 * Parse Gemini's JSON response for contract analysis
 */
function parseContractResponse(text: string): ContractAnalysis {
  try {
    // Extract JSON from the response (sometimes it's wrapped in markdown code blocks)
    let jsonText = text.trim();
    
    // Remove markdown code blocks if present
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.replace(/^```json\n/, '').replace(/\n```$/, '');
    } else if (jsonText.startsWith('```')) {
      jsonText = jsonText.replace(/^```\n/, '').replace(/\n```$/, '');
    }
    
    const parsed = JSON.parse(jsonText);
    
    // Convert string dates to Date objects
    const contractDate = parsed.contractDate ? new Date(parsed.contractDate) : null;
    const expiryDate = parsed.expiryDate ? new Date(parsed.expiryDate) : null;

    return {
      summary: parsed.summary || 'No summary available',
      contractDate: contractDate && !isNaN(contractDate.getTime()) ? contractDate : null,
      expiryDate: expiryDate && !isNaN(expiryDate.getTime()) ? expiryDate : null,
      keyInformation: parsed.keyInformation || {},
    };
  } catch (error) {
    console.error('Error parsing Gemini contract response:', error);
    // Return a fallback response
    return {
      summary: text.substring(0, 500), // Use raw text as summary if JSON parsing fails
      contractDate: null,
      expiryDate: null,
      keyInformation: {},
    };
  }
}

/**
 * Parse Gemini's JSON response for checklist extraction
 */
function parseChecklistExtractionResponse(text: string): ChecklistExtractionResult {
  try {
    let jsonText = text.trim();

    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.replace(/^```json\s*/, '').replace(/```\s*$/, '');
    } else if (jsonText.startsWith('```')) {
      jsonText = jsonText.replace(/^```\s*/, '').replace(/```\s*$/, '');
    }

    const parsed = JSON.parse(jsonText);

    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      description: typeof parsed.description === 'string' ? parsed.description : undefined,
      items: Array.isArray(parsed.items) ? parsed.items : [],
      variables: Array.isArray(parsed.variables) ? parsed.variables : undefined,
      confidenceNotes: typeof parsed.confidenceNotes === 'string' ? parsed.confidenceNotes : undefined,
    };
  } catch (error) {
    console.error('Error parsing checklist extraction response:', error);
    return {
      name: '',
      description: undefined,
      items: [],
      variables: undefined,
      confidenceNotes: `Raw response: ${text.substring(0, 500)}`,
    };
  }
}

/**
 * Extract table/column data from PDF reports for asset import
 */
export interface PDFTableExtraction {
  columns: string[];
  rows: Record<string, string>[];
  detectedAssetNameColumn?: string;
  detectedLocationColumn?: string;
  summary: string;
}

export async function extractTableFromPDF(file: File): Promise<PDFTableExtraction> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    const fileBase64 = await fileToBase64(file);

    if (file.type !== 'application/pdf') {
      throw new Error('Only PDF files are supported for table extraction.');
    }

    const prompt = `Analyze this PDF document and extract all tabular data (tables, lists, or structured data) that could represent assets or equipment.

Your task:
1. Identify ALL tables or structured data in the document
2. Extract column headers from the first table you find
3. Extract all data rows from that table (limit to first 100 rows if very large)
4. Identify which column likely contains asset names/equipment names (look for columns like "Asset Name", "Equipment", "Item", "Description", "Name", etc.)
5. Identify which column might contain location information (look for "Location", "Site", "Building", "Room", etc.)
6. If multiple tables exist, focus on the one that appears to be an asset/equipment list

CRITICAL: Return ONLY valid JSON. No markdown, no explanations, no code blocks. Just pure JSON.

Return the data in this exact JSON format:
{
  "columns": ["Column1", "Column2", "Column3"],
  "rows": [
    {"Column1": "value1", "Column2": "value2"},
    {"Column1": "value2", "Column2": "value3"}
  ],
  "detectedAssetNameColumn": "Column1",
  "detectedLocationColumn": "Column2",
  "summary": "Brief description"
}

Rules:
- Extract ALL rows from the table (max 100 rows)
- Preserve exact column names as they appear in the document
- Escape all quotes in values: use \\" for quotes inside text
- Ensure all row objects have the same keys (matching column names)
- Use null (not "null" string) if columns are not detected
- Return valid JSON only - no markdown formatting, no code blocks`;

    let parsedResult: any;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[extractTableFromPDF] ✅ Using Vertex AI');
      const fileData = {
          data: fileBase64.data,
          mimeType: 'application/pdf',
      };
      parsedResult = await callVertexAIProxy(prompt, fileData, true);
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[extractTableFromPDF] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    // Process the parsed result
    const text = typeof parsedResult === 'string' ? parsedResult : JSON.stringify(parsedResult);
    return parseTableExtractionResponse(text);
  } catch (error: any) {
    console.error('Error extracting table from PDF:', error);
    throw new Error(`Failed to extract table from PDF: ${error.message}`);
  }
}

/**
 * Parse Gemini's JSON response for table extraction
 */
function parseTableExtractionResponse(text: string): PDFTableExtraction {
  try {
    let jsonText = text.trim();
    
    // Log raw response for debugging (first 500 chars)
    console.log('Raw Gemini response (first 500 chars):', jsonText.substring(0, 500));

    // Remove markdown code blocks if present
    if (jsonText.includes('```json')) {
      const jsonMatch = jsonText.match(/```json\s*([\s\S]*?)\s*```/);
      if (jsonMatch && jsonMatch[1]) {
        jsonText = jsonMatch[1].trim();
      } else {
        jsonText = jsonText.replace(/^```json\s*/i, '').replace(/\s*```$/i, '');
      }
    } else if (jsonText.includes('```')) {
      const codeMatch = jsonText.match(/```\s*([\s\S]*?)\s*```/);
      if (codeMatch && codeMatch[1]) {
        jsonText = codeMatch[1].trim();
      } else {
        jsonText = jsonText.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }
    }

    // Try to extract JSON object if response contains text before/after
    const jsonObjectMatch = jsonText.match(/\{[\s\S]*\}/);
    if (jsonObjectMatch) {
      jsonText = jsonObjectMatch[0];
    }

    // Clean up common JSON issues - but be careful with quotes
    jsonText = jsonText
      .replace(/,\s*}/g, '}') // Remove trailing commas before }
      .replace(/,\s*]/g, ']') // Remove trailing commas before ]
      .trim();
    
    // Only replace single quotes if they're used for property names (not inside strings)
    // This is a safer approach - only fix obvious cases
    if (!jsonText.includes('"') && jsonText.includes("'")) {
      // If no double quotes but has single quotes, try replacing
      jsonText = jsonText.replace(/'/g, '"');
    }

    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch (parseError) {
      // Try to fix common JSON issues
      console.warn('Initial JSON parse failed, attempting to fix...', parseError);
      
      // Try to extract just the JSON part more aggressively
      const jsonStart = jsonText.indexOf('{');
      const jsonEnd = jsonText.lastIndexOf('}');
      if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
        jsonText = jsonText.substring(jsonStart, jsonEnd + 1);
        try {
          parsed = JSON.parse(jsonText);
        } catch (secondError) {
          // Last resort: try to manually extract data
          console.error('JSON parsing failed after fixes:', secondError);
          throw new Error(`Invalid JSON response from AI. The PDF may not contain structured table data, or the AI response was malformed. Please try again or use a different PDF.`);
        }
      } else {
        throw parseError;
      }
    }

    // Ensure columns is an array
    const columns = Array.isArray(parsed.columns) ? parsed.columns : [];
    
    // Ensure rows is an array of objects
    const rows = Array.isArray(parsed.rows) 
      ? parsed.rows.filter((row: any) => typeof row === 'object' && row !== null)
      : [];

    // Validate we have at least some data
    if (columns.length === 0 && rows.length === 0) {
      throw new Error('No table data found in the extracted response. The PDF may not contain structured table data.');
    }

    return {
      columns,
      rows,
      detectedAssetNameColumn: parsed.detectedAssetNameColumn || undefined,
      detectedLocationColumn: parsed.detectedLocationColumn || undefined,
      summary: parsed.summary || 'Table data extracted from PDF',
    };
  } catch (error: any) {
    console.error('Error parsing table extraction response:', error);
    console.error('Response text length:', text.length);
    console.error('Response preview:', text.substring(0, 1000));
    throw new Error(error?.message || 'Failed to parse extracted table data. The PDF may not contain structured table data.');
  }
}

/**
 * Vendor information extracted from contract
 */
export interface VendorExtraction {
  vendorName: string;
  contact?: string;
  email?: string;
  phone?: string;
  category?: string;
  address?: string;
  companyName?: string;
}

/**
 * Document classification result
 */
export interface DocumentClassification {
  documentType: 'contract' | 'communication' | 'amendment' | 'certificate' | 'license' | 'other';
  confidence: 'high' | 'medium' | 'low';
  reason?: string; // Why it was classified this way
  isMainContract?: boolean; // True if this is likely the main contract document
}

/**
 * Extract vendor information from a contract document
 * This is used for bulk upload to automatically create vendor profiles
 */
/**
 * Classify a document to determine its type (contract, communication, amendment, etc.)
 */
export async function classifyDocument(file: File, folderName?: string): Promise<DocumentClassification> {
    const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isWord = file.type === 'application/msword' || 
                   file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                   file.name.toLowerCase().endsWith('.doc') ||
                   file.name.toLowerCase().endsWith('.docx');

    if (!isPDF && !isWord) {
      // For non-PDF/Word files, use filename heuristics
      const fileName = file.name.toLowerCase();
      if (fileName.includes('email') || fileName.includes('letter') || fileName.includes('communication')) {
        return { documentType: 'communication', confidence: 'medium', isMainContract: false };
      }
      if (fileName.includes('amendment') || fileName.includes('addendum')) {
        return { documentType: 'amendment', confidence: 'medium', isMainContract: false };
      }
      if (fileName.includes('certificate') || fileName.includes('cert')) {
        return { documentType: 'certificate', confidence: 'high', isMainContract: false };
      }
      if (fileName.includes('license') || fileName.includes('lic')) {
        return { documentType: 'license', confidence: 'high', isMainContract: false };
      }
      return { documentType: 'other', confidence: 'low', isMainContract: false };
    }

  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    const fileBase64 = await fileToBase64(file);

    const prompt = `Analyze this document and classify its type. The document is from a folder${folderName ? ` named "${folderName}"` : ''}.

Classify the document as one of these types:
- "contract": Main contract/agreement document (service agreement, vendor contract, lease, etc.)
- "communication": Email, letter, or other correspondence
- "amendment": Amendment, addendum, or modification to a contract
- "certificate": Certificate document (insurance, compliance, etc.)
- "license": License or permit document
- "other": Any other type of document

Consider:
- File name patterns
- Document structure and content
- Presence of signatures, terms, dates
- Whether this appears to be the main contract vs supporting document

Respond in JSON format:
{
  "documentType": "contract | communication | amendment | certificate | license | other",
  "confidence": "high | medium | low",
  "reason": "Brief explanation of classification",
  "isMainContract": true or false (true only if this is clearly the main contract document)
}`;

    let parsed: any;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[classifyDocument] ✅ Using Vertex AI');
      const mimeType = isPDF ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const fileData = {
          data: fileBase64.data,
          mimeType: mimeType,
      };
      parsed = await callVertexAIProxy(prompt, fileData, true);
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[classifyDocument] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    return {
      documentType: parsed.documentType || 'other',
      confidence: parsed.confidence || 'medium',
      reason: parsed.reason,
      isMainContract: parsed.isMainContract === true,
    };
  } catch (error: any) {
    console.error('Error classifying document:', error);
    // Fallback: use filename heuristics
    const fileName = file.name.toLowerCase();
    if (fileName.includes('contract') || fileName.includes('agreement')) {
      return { documentType: 'contract', confidence: 'low', isMainContract: true };
    }
    if (fileName.includes('email') || fileName.includes('letter')) {
      return { documentType: 'communication', confidence: 'low', isMainContract: false };
    }
    return { documentType: 'other', confidence: 'low', isMainContract: false };
  }
}

/**
 * Extract vendor name from folder name
 * Example: "Hilton Garden Inn Contracts" -> "Hilton Garden Inn"
 */
export function extractVendorNameFromFolder(folderName: string): string {
  // Remove common suffixes
  const cleaned = folderName
    .replace(/\s*(contracts?|agreements?|docs?|documents?|files?)\s*$/i, '')
    .replace(/\s*-\s*.*$/, '') // Remove anything after dash
    .trim();
  
  return cleaned || folderName;
}

/**
 * Extract contact information from multiple files
 * Scans all files and merges contact information
 */
export async function extractContactFromMultipleFiles(files: File[]): Promise<{
  contact?: string;
  email?: string;
  phone?: string;
  confidence: 'high' | 'medium' | 'low';
}> {
  if ((!USE_VERTEX_AI && !genAI) || files.length === 0) {
    return { confidence: 'low' };
  }

  try {
    const contactInfo: { contact?: string; email?: string; phone?: string }[] = [];
    
    // Process up to 5 files to extract contact info (to avoid rate limits)
    const filesToProcess = files.slice(0, 5);
    
    for (const file of filesToProcess) {
      try {
        const isPDF = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        const isWord = file.type === 'application/msword' || 
                       file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                       file.name.toLowerCase().endsWith('.doc') ||
                       file.name.toLowerCase().endsWith('.docx');

        if (!isPDF && !isWord) continue;

        const prompt = `Extract contact information from this document. Look for:
- Contact person name
- Email address
- Phone number

Respond in JSON format:
{
  "contact": "name or null",
  "email": "email@example.com or null",
  "phone": "phone number or null"
}`;

        let parsed: { contact?: string; email?: string; phone?: string };
        
        if (USE_VERTEX_AI) {
          // Use Vertex AI through proxy (enterprise security)
          console.log(`[extractContactFromMultipleFiles] ✅ Using Vertex AI for ${file.name}`);
          if (isWord) {
            const docxText = await extractTextFromDocx(file);
            parsed = await callVertexAIProxy([prompt, docxText], undefined, true) as { contact?: string; email?: string; phone?: string };
          } else {
            const fileBase64 = await fileToBase64(file);
            const fileData = {
              data: fileBase64.data,
              mimeType: 'application/pdf',
            };
            parsed = await callVertexAIProxy(prompt, fileData, true) as { contact?: string; email?: string; phone?: string };
          }
        } else {
          // SECURITY: Public Gemini API is DISABLED
          console.error('[extractContactFromMultipleFiles] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
          throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
        }
        
        if (parsed.contact || parsed.email || parsed.phone) {
          contactInfo.push(parsed);
        }
      } catch (error) {
        console.error(`Error extracting contact from ${file.name}:`, error);
        // Continue with next file
      }
    }

    // Merge contact information (prioritize non-null values)
    const merged: { contact?: string; email?: string; phone?: string } = {};
    const contacts = contactInfo.map(c => c.contact).filter((c): c is string => Boolean(c));
    const emails = contactInfo.map(c => c.email).filter((e): e is string => Boolean(e));
    const phones = contactInfo.map(c => c.phone).filter((p): p is string => Boolean(p));

    if (contacts.length > 0) {
      merged.contact = contacts[0]; // Use first found
    }
    if (emails.length > 0) {
      // Use first valid email
      const validEmail = emails.find(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      merged.email = validEmail || emails[0];
    }
    if (phones.length > 0) {
      merged.phone = phones[0]; // Use first found
    }

    const confidence = contactInfo.length > 0 ? (contactInfo.length >= 2 ? 'high' : 'medium') : 'low';
    
    return {
      ...merged,
      confidence,
    };
  } catch (error: any) {
    console.error('Error extracting contact from multiple files:', error);
    return { confidence: 'low' };
  }
}

export async function extractVendorFromContract(file: File): Promise<VendorExtraction> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    const isPDF = file.type === 'application/pdf';
    const isWord = file.type === 'application/msword' || 
                   file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                   file.name.toLowerCase().endsWith('.doc') ||
                   file.name.toLowerCase().endsWith('.docx');

    if (!isPDF && !isWord) {
      throw new Error('Unsupported file type. Please upload PDF or Word documents.');
    }

    const prompt = `Analyze this vendor contract/agreement document and extract vendor information.

Your task is to identify the VENDOR (the service provider, supplier, or contractor) in this contract, NOT the client/customer.

Extract the following information about the VENDOR:
1. **Vendor Name/Company Name**: The name of the vendor company or business (this is the service provider, not the client)
2. **Contact Person**: Name of the primary contact person at the vendor company
3. **Email**: Vendor's email address
4. **Phone**: Vendor's phone number
5. **Category**: Type of service the vendor provides (e.g., "Fire Protection", "Elevator", "Maintenance", "Security", "HVAC", "Plumbing", "Electrical", "Landscaping", etc.)
6. **Address**: Vendor's business address (if available)

IMPORTANT:
- Identify which party is the VENDOR (service provider) vs the CLIENT (customer)
- The vendor is typically the party providing services/products
- Look for terms like "Service Provider", "Contractor", "Supplier", "Vendor" in the contract
- If the contract mentions "we" or "our company" providing services, that's likely the vendor
- Extract information from signature blocks, letterhead, or contact sections

Please respond in JSON format:
{
  "vendorName": "Company name of the vendor (REQUIRED - use filename if not found)",
  "contact": "Contact person name or null",
  "email": "Email address or null",
  "phone": "Phone number or null",
  "category": "Service category or null",
  "address": "Business address or null",
  "companyName": "Same as vendorName or null"
}

CRITICAL: You MUST provide a vendorName. If you cannot find it in the document, use a cleaned version of the filename (remove extension, remove common words like 'contract', 'agreement', etc.).`;

    let parsedResult: any;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[extractVendorFromContract] ✅ Using Vertex AI');
      if (isWord) {
        const docxText = await extractTextFromDocx(file);
        parsedResult = await callVertexAIProxy([prompt, docxText], undefined, true);
      } else {
        const fileBase64 = await fileToBase64(file);
        const fileData = {
          data: fileBase64.data,
          mimeType: 'application/pdf',
        };
        parsedResult = await callVertexAIProxy(prompt, fileData, true);
      }
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[extractVendorFromContract] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    // Process the parsed result
    const text = typeof parsedResult === 'string' ? parsedResult : JSON.stringify(parsedResult);
    return parseVendorExtractionResponse(text, file.name);
  } catch (error: any) {
    console.error('Error extracting vendor from contract:', error);
    // Fallback: use filename as vendor name
    const fallbackName = file.name
      .replace(/\.(pdf|docx?)$/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b(contract|agreement|vendor|doc)\b/gi, '')
      .trim() || 'Unknown Vendor';
    
    return {
      vendorName: fallbackName,
    };
  }
}

/**
 * Parse Gemini's JSON response for vendor extraction
 */
function parseVendorExtractionResponse(text: string, fileName: string): VendorExtraction {
  try {
    let jsonText = text.trim();
    
    // Remove markdown code blocks if present
    if (jsonText.startsWith('```json')) {
      jsonText = jsonText.replace(/^```json\n/, '').replace(/\n```$/, '');
    } else if (jsonText.startsWith('```')) {
      jsonText = jsonText.replace(/^```\n/, '').replace(/\n```$/, '');
    }
    
    const parsed = JSON.parse(jsonText);
    
    // Ensure vendorName exists - use filename as fallback
    let vendorName = parsed.vendorName;
    if (!vendorName || vendorName.trim() === '' || vendorName === 'null') {
      vendorName = fileName
        .replace(/\.(pdf|docx?)$/i, '')
        .replace(/[_-]+/g, ' ')
        .replace(/\b(contract|agreement|vendor|doc)\b/gi, '')
        .trim() || 'Unknown Vendor';
    }
    
    return {
      vendorName: vendorName.trim(),
      contact: parsed.contact && parsed.contact !== 'null' ? parsed.contact.trim() : undefined,
      email: parsed.email && parsed.email !== 'null' ? parsed.email.trim() : undefined,
      phone: parsed.phone && parsed.phone !== 'null' ? parsed.phone.trim() : undefined,
      category: parsed.category && parsed.category !== 'null' ? parsed.category.trim() : undefined,
      address: parsed.address && parsed.address !== 'null' ? parsed.address.trim() : undefined,
      companyName: parsed.companyName && parsed.companyName !== 'null' ? parsed.companyName.trim() : undefined,
    };
  } catch (error) {
    console.error('Error parsing vendor extraction response:', error);
    // Fallback: use filename as vendor name
    const fallbackName = fileName
      .replace(/\.(pdf|docx?)$/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\b(contract|agreement|vendor|doc)\b/gi, '')
      .trim() || 'Unknown Vendor';
    
    return {
      vendorName: fallbackName,
    };
  }
}

/**
 * Check if Gemini is configured
 */
/**
 * Identify the Item column from spreadsheet headers
 * Returns the index and name of the column that contains items
 */
export interface ItemColumnIdentificationResult {
  itemColumnIndex: number;
  itemColumnName: string;
  confidenceNotes?: string;
}

export async function identifyItemColumn(headers: string[]): Promise<ItemColumnIdentificationResult> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    const prompt = `You are an expert assistant helping to identify which column in a spreadsheet contains "Items" (the main items/rows that will be tracked).

Given these column headers from a spreadsheet:
${JSON.stringify(headers, null, 2)}

Identify which column is the "Item" column. The Item column typically:
- Contains product names, item names, or inventory items
- Is the primary identifier for each row (e.g., "Item", "Product", "Name", "Description")
- May be named: "Item", "Item #", "Product", "Name", "Description", "Item Name", "Product Name", etc.
- Is usually one of the first columns but not always

CRITICAL: Return ONLY valid JSON. No markdown, no explanations, no code blocks. Just pure JSON.

Return the data in this exact JSON format:
{
  "itemColumnIndex": 0,
  "itemColumnName": "Item",
  "confidenceNotes": "Optional notes about the identification"
}

Rules:
- itemColumnIndex: The zero-based index of the column (0 = first column, 1 = second column, etc.)
- itemColumnName: The exact header name from the provided headers array
- If no clear Item column is found, choose the first column that seems most likely to contain item names
- confidenceNotes: Optional explanation of why this column was chosen

Return ONLY valid JSON conforming to the schema.`;

    let parsed: ItemColumnIdentificationResult;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[identifyItemColumn] ✅ Using Vertex AI');
      const result = await callVertexAIProxy(prompt, undefined, true);
      parsed = result as ItemColumnIdentificationResult;
    } else {
      // SECURITY: Public Gemini API is DISABLED
      console.error('[identifyItemColumn] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    // Validate parsed result
    try {
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Invalid response format');
      }
    } catch (parseError) {
      console.error('Failed to parse AI response:', parseError);
      // Fallback: try to find Item column heuristically
      const itemColumnIndex = headers.findIndex(h => 
        h.toLowerCase().includes('item') || 
        h.toLowerCase().includes('product') || 
        h.toLowerCase() === 'name'
      );
      return {
        itemColumnIndex: itemColumnIndex >= 0 ? itemColumnIndex : 0,
        itemColumnName: headers[itemColumnIndex >= 0 ? itemColumnIndex : 0] || headers[0] || 'Item',
        confidenceNotes: 'AI parsing failed, used heuristic fallback'
      };
    }

    // Validate the result
    if (typeof parsed.itemColumnIndex !== 'number' || parsed.itemColumnIndex < 0 || parsed.itemColumnIndex >= headers.length) {
      // Fallback to first column
      return {
        itemColumnIndex: 0,
        itemColumnName: headers[0] || 'Item',
        confidenceNotes: 'AI returned invalid index, using first column'
      };
    }

    return {
      itemColumnIndex: parsed.itemColumnIndex,
      itemColumnName: parsed.itemColumnName || headers[parsed.itemColumnIndex] || 'Item',
      confidenceNotes: parsed.confidenceNotes
    };
  } catch (error: any) {
    console.error('Error identifying item column:', error);
    // Fallback: use heuristic
    const itemColumnIndex = headers.findIndex(h => 
      h.toLowerCase().includes('item') || 
      h.toLowerCase().includes('product') || 
      h.toLowerCase() === 'name'
    );
    return {
      itemColumnIndex: itemColumnIndex >= 0 ? itemColumnIndex : 0,
      itemColumnName: headers[itemColumnIndex >= 0 ? itemColumnIndex : 0] || headers[0] || 'Item',
      confidenceNotes: 'Error occurred, used heuristic fallback'
    };
  }
}

export function isGeminiConfigured(): boolean {
  return !!apiKey && !!genAI;
}

/**
 * Extract field values from a document based on field definitions
 * This is used to auto-populate evidence fields when files are uploaded
 */
/**
 * Check if a file name matches the fileNameKey pattern
 * Supports multiple matching strategies:
 * 1. Case-insensitive substring matching (default)
 * 2. Exact match (if fileNameKey matches entire filename without extension)
 * 3. Word boundary matching (if fileNameKey appears as a complete word)
 * 
 * Examples:
 * - fileNameKey="loan-number" matches:
 *   - "loan-number-application.pdf" ✅ (substring)
 *   - "loan_number_doc.pdf" ✅ (substring with underscore)
 *   - "loanNumberFile.pdf" ✅ (camelCase)
 *   - "LOAN-NUMBER.pdf" ✅ (case-insensitive)
 *   - "loan-number.pdf" ✅ (exact match)
 *   - "appraisal-loan-number-report.pdf" ✅ (substring)
 * 
 * @param fileName - The file name to check (e.g., "loan-number-12345.pdf")
 * @param fileNameKey - The key pattern to match (e.g., "loan-number")
 * @returns true if the file name matches the pattern
 */
export function fileNameMatchesKey(fileName: string, fileNameKey: string): boolean {
  if (!fileNameKey || !fileName) return false;
  
  // Remove file extension and normalize
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '').trim();
  const key = fileNameKey.trim();
  
  if (!nameWithoutExt || !key) return false;
  
  // Convert to lowercase for case-insensitive comparison
  const nameLower = nameWithoutExt.toLowerCase();
  const keyLower = key.toLowerCase();
  
  // Strategy 1: Exact match (case-insensitive)
  // If fileNameKey matches the entire filename (without extension)
  if (nameLower === keyLower) {
    return true;
  }
  
  // Strategy 2: Word boundary matching
  // Check if fileNameKey appears as a complete word (not part of another word)
  // Matches: "loan-number" in "loan-number-12345" or "prefix-loan-number-suffix"
  // Doesn't match: "loan" in "loaner" or "number" in "renumber"
  const wordBoundaryRegex = new RegExp(`\\b${escapeRegex(keyLower)}\\b`, 'i');
  if (wordBoundaryRegex.test(nameWithoutExt)) {
    return true;
  }
  
  // Strategy 3: Substring matching (case-insensitive)
  // Check if fileNameKey appears anywhere in the filename
  // This is the most permissive matching
  if (nameLower.includes(keyLower)) {
    return true;
  }
  
  // Strategy 4: Handle common separators (hyphens, underscores, spaces)
  // Normalize separators and check again
  const normalizedName = nameLower.replace(/[-_\s]+/g, '-');
  const normalizedKey = keyLower.replace(/[-_\s]+/g, '-');
  
  if (normalizedName.includes(normalizedKey)) {
    return true;
  }
  
  return false;
}

/**
 * Escape special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Get fileNameKeys array from field definition (handles backward compatibility)
 * Returns array of fileNameKeys, checking both fileNameKeys (new) and fileNameKey (legacy)
 */
export function getFileNameKeys(fieldDef: { fileNameKey?: string; fileNameKeys?: string[] }): string[] {
  // Prefer fileNameKeys (new format)
  if (fieldDef.fileNameKeys && fieldDef.fileNameKeys.length > 0) {
    return fieldDef.fileNameKeys.filter(key => key && key.trim().length > 0);
  }
  
  // Fall back to fileNameKey (legacy format) for backward compatibility
  if (fieldDef.fileNameKey && fieldDef.fileNameKey.trim().length > 0) {
    return [fieldDef.fileNameKey.trim()];
  }
  
  return [];
}

/**
 * Check if a file name matches any of the fileNameKeys
 * Returns true if the file matches ANY of the keys (OR logic)
 * Checks keys in order - first match wins
 * 
 * @param fileName - The file name to check
 * @param fileNameKeys - Array of keys to check against
 * @returns true if file matches any key, false otherwise
 */
export function fileNameMatchesAnyKey(fileName: string, fileNameKeys: string[]): boolean {
  if (!fileNameKeys || fileNameKeys.length === 0 || !fileName) {
    return false;
  }
  
  // Check each key in order - first match wins
  for (const key of fileNameKeys) {
    if (fileNameMatchesKey(fileName, key)) {
      return true;
    }
  }
  
  return false;
}

export async function extractFieldValuesFromDocument(
  file: File,
  fieldDefinitions: Array<{
    key: string;
    label: string;
    type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list';
    fileNameKey?: string;
    fileNameKeys?: string[];
    enumValues?: string[];
    workflowRules?: string;
    extractionHints?: {
      keywords?: string[];
      anchors?: string[];
      regex?: string;
    };
  }>
): Promise<FieldExtractionResult> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  if (!fieldDefinitions || fieldDefinitions.length === 0) {
    return { fieldValues: {}, confidence: 'low', notes: 'No field definitions provided' };
  }

  // Check if this file matches any field's fileNameKey/fileNameKeys
  // If a field has fileNameKey or fileNameKeys, we only process files that match
  const fieldsToExtract = fieldDefinitions.filter(field => {
    const fileNameKeys = getFileNameKeys(field);
    if (fileNameKeys.length > 0) {
      // Only extract this field if the file name matches any of the fileNameKeys
      return fileNameMatchesAnyKey(file.name, fileNameKeys);
    }
    // No fileNameKey restriction, process this field
    return true;
  });

  if (fieldsToExtract.length === 0) {
    return {
      fieldValues: {},
      confidence: 'low',
      notes: 'No fields match this file (fileNameKey filtering)',
    };
  }

  try {
    const mimeType = file.type;
    const isPDF = mimeType === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isWord = mimeType === 'application/msword' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.name.toLowerCase().endsWith('.doc') ||
      file.name.toLowerCase().endsWith('.docx');
    const isImage = mimeType?.startsWith('image/');

    if (!isPDF && !isWord && !isImage) {
      throw new Error('Unsupported file type. Please upload PDF, Word, or image files.');
    }

    // Build field extraction instructions (include enum allowed values so AI returns valid enum values)
    const fieldInstructions = fieldsToExtract.map(field => {
      let instruction = `- ${field.key} (${field.label}): Type: ${field.type}`;
      if (field.type === 'list') {
        instruction += ', Extract as a JSON array of strings (one element per item, e.g. ["Name 1", "Name 2"]). Do not use commas inside an element—each array element is one full item (e.g. one guarantor name).';
      }
      if (field.type === 'enum' && field.enumValues && field.enumValues.length > 0) {
        instruction += `, Allowed values (use exactly one): ${field.enumValues.join(', ')}`;
      }
      if (field.extractionHints?.keywords && field.extractionHints.keywords.length > 0) {
        instruction += `, Look for keywords: ${field.extractionHints.keywords.join(', ')}`;
      }
      if (field.extractionHints?.anchors && field.extractionHints.anchors.length > 0) {
        instruction += `, Look near: ${field.extractionHints.anchors.join(', ')}`;
      }
      if (field.extractionHints?.regex) {
        instruction += `, Pattern: ${field.extractionHints.regex}`;
      }
      return instruction;
    }).join('\n');

    const prompt = `You are an expert document analyzer. Analyze this document and extract specific field values.

FIELD DEFINITIONS TO EXTRACT:
${fieldInstructions}

INSTRUCTIONS:
1. For each field, search the document for the value using the provided hints (keywords, anchors, regex patterns).
2. Extract values matching the field type:
   - date: Extract dates in YYYY-MM-DD format
   - string: Extract text values
   - number: Extract numeric values
   - boolean: Extract yes/no, true/false, or similar boolean values
   - enum: Use ONLY one of the allowed values listed for that field (e.g. if allowed values are "In", "Out", return exactly "In" or "Out", not "yes" or other text)
   - list: Return a JSON array of strings, one element per item (e.g. ["DHAVAL CHOKSKI", "PURVISH SONI"]. Names or items that contain commas stay as a single array element.)
3. If a field cannot be found, set it to null.
4. Return confidence level: "high" if most fields found, "medium" if some found, "low" if few/none found.

Return ONLY valid JSON in this exact format:
{
  "fieldValues": {
    "fieldKey1": "extracted value or null",
    "fieldKey2": "extracted value or null"
  },
  "confidence": "high | medium | low",
  "notes": "Optional notes about extraction quality"
}`;

    let parsed: any;
    let serviceUsed: 'VERTEX_AI' | 'PUBLIC_GEMINI' = 'VERTEX_AI';
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[extractFieldValuesFromDocument] ✅ Using Vertex AI (Enterprise Secure)');
      let fileData: { data: string; mimeType: string } | undefined;
    
    // Handle Word documents by extracting text first
    if (isWord) {
      const docxText = await extractTextFromDocx(file);
        // For Word docs, send as text prompt (no file data)
        const result = await callVertexAIProxy([prompt, docxText], undefined, true);
        parsed = result;
    } else {
      // For PDF and images, send as binary
      const fileBase64 = await fileToBase64(file);
        fileData = {
            data: fileBase64.data,
            mimeType: isPDF ? 'application/pdf' : fileBase64.mimeType,
        };
        const result = await callVertexAIProxy(prompt, fileData, true);
        parsed = result;
      }
      serviceUsed = 'VERTEX_AI';
    } else {
      // SECURITY: Public Gemini API is DISABLED for bank security compliance
      // This code path should NEVER execute in production
      console.error('[extractFieldValuesFromDocument] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      console.error('[extractFieldValuesFromDocument] ⚠️ USE_VERTEX_AI must be true. Public API fallback is NOT allowed for bank data.');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled for bank security compliance. Vertex AI must be used. Set USE_VERTEX_AI=true.');
      
      // NOTE: Public API code has been removed for security compliance
      // All bank data must go through Vertex AI for enterprise data governance
    }
    
    // Normalize field values based on type
    const normalizedValues: Record<string, any> = {};
    fieldsToExtract.forEach(field => {
      const rawValue = parsed.fieldValues?.[field.key];
      if (rawValue === null || rawValue === undefined || rawValue === 'null') {
        normalizedValues[field.key] = null;
      } else if (field.type === 'date') {
        // Try to parse date
        try {
          const date = new Date(rawValue);
          if (!isNaN(date.getTime())) {
            normalizedValues[field.key] = date.toISOString().split('T')[0]; // YYYY-MM-DD
          } else {
            normalizedValues[field.key] = null;
          }
        } catch {
          normalizedValues[field.key] = null;
        }
      } else if (field.type === 'number') {
        const num = Number(rawValue);
        normalizedValues[field.key] = isNaN(num) ? null : num;
      } else if (field.type === 'boolean') {
        const str = String(rawValue).toLowerCase();
        normalizedValues[field.key] = str === 'true' || str === 'yes' || str === '1';
      } else if (field.type === 'list') {
        // Normalize to string[]: already array, JSON string, or newline/comma-separated
        if (Array.isArray(rawValue)) {
          normalizedValues[field.key] = rawValue.map(item => item == null ? '' : String(item).trim()).filter(Boolean);
        } else if (typeof rawValue === 'string') {
          const trimmed = rawValue.trim();
          if (trimmed.startsWith('[')) {
            try {
              const arr = JSON.parse(trimmed);
              normalizedValues[field.key] = Array.isArray(arr) ? arr.map((item: any) => String(item == null ? '' : item).trim()).filter(Boolean) : [trimmed];
            } catch {
              normalizedValues[field.key] = [trimmed];
            }
          } else {
            normalizedValues[field.key] = trimmed.split(/\n|,/).map(s => s.trim()).filter(Boolean);
          }
        } else {
          normalizedValues[field.key] = [];
        }
      } else {
        normalizedValues[field.key] = String(rawValue);
      }
    });

    const result: FieldExtractionResult = {
      fieldValues: normalizedValues,
      confidence: parsed.confidence || 'low',
      notes: parsed.notes,
      serviceUsed: serviceUsed,
      timestamp: new Date().toISOString(),
    };
    
    // Log service verification
    console.log('[extractFieldValuesFromDocument] ✅ Extraction complete:', {
      serviceUsed,
      fieldsExtracted: Object.keys(normalizedValues).length,
      confidence: result.confidence,
      timestamp: result.timestamp,
    });
    
    // Security check: Ensure Vertex AI was used
    if (serviceUsed !== 'VERTEX_AI') {
      console.error('[extractFieldValuesFromDocument] ⚠️ SECURITY WARNING: Public Gemini API was used instead of Vertex AI!');
      throw new Error('SECURITY VIOLATION: Public Gemini API cannot be used for bank data. Vertex AI must be used.');
    }
    
    return result;
  } catch (error: any) {
    console.error('Error extracting field values from document:', error);
    return {
      fieldValues: {},
      confidence: 'low',
      notes: `Error: ${error.message}`,
    };
  }
}

/**
 * @deprecated extractFactsFromDocument has been removed. Use extractFieldValuesFromDocument instead.
 * The old bank-specific fact extraction is no longer needed - all fields now come from checklist definitions.
 */

/**
 * @deprecated extractFactsFromDocumentWithChecklistFields has been removed. Use extractFieldValuesFromDocument instead.
 * This function was replaced by extractFieldValuesFromDocument which provides the same functionality with a cleaner interface.
 */

/**
 * Checklist item evaluation result from AI
 */
export interface ChecklistItemEvaluationResult {
  status: 'PASS' | 'FAIL' | 'NA' | 'NEEDS_REVIEW';
  findingText: string;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string; // AI's detailed explanation
}

/**
 * Evaluate a checklist item using AI based on evidence fields and asset facts
 * 
 * @param item - Checklist item to evaluate
 * @param extractedFields - Evidence fields extracted from asset facts (exact key matching)
 * @param facts - All asset facts for additional context
 * @returns AI evaluation result with status, finding text, and reasoning
 */
export async function evaluateChecklistItemWithAI(
  item: ChecklistItem,
  extractedFields: ExtractedEvidenceFields,
  facts: Record<string, any>
): Promise<ChecklistItemEvaluationResult> {
  if (!USE_VERTEX_AI && !genAI) {
    throw new Error('Gemini API key not configured. Please set VITE_GEMINI_API_KEY in your .env file.');
  }

  try {
    let serviceUsed: 'VERTEX_AI' | 'PUBLIC_GEMINI' = 'VERTEX_AI';
    
    // Build evidence fields section for prompt
    let evidenceFieldsSection = '';
    for (const evidenceDef of item.evidenceDefinitions || []) {
      // Try evidenceName first, then categoryCode (legacy), then fallback
      const evidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
      const evidenceFieldsData = extractedFields[evidenceName] || {};
      
      // Log for debugging
      if (Object.keys(evidenceFieldsData).length === 0) {
        console.log(`[evaluateChecklistItemWithAI] No fields found for evidence: ${evidenceName}, available evidence names in extractedFields: ${Object.keys(extractedFields).join(', ')}`);
      } else {
        console.log(`[evaluateChecklistItemWithAI] Found ${Object.keys(evidenceFieldsData).length} fields for evidence: ${evidenceName}`);
      }
      
      evidenceFieldsSection += `\n**Evidence: ${evidenceName}**\n`;
      evidenceFieldsSection += `Required: ${evidenceDef.required ? 'Yes' : 'No'}\n`;
      if (evidenceDef.description) {
        evidenceFieldsSection += `Description: ${evidenceDef.description}\n`;
      }
      // Add workflow rules if provided
      if (evidenceDef.workflowRules) {
        evidenceFieldsSection += `\n**Workflow Rules:**\n${evidenceDef.workflowRules}\n`;
        evidenceFieldsSection += `\nIMPORTANT: Apply these workflow rules when evaluating this evidence. `;
        evidenceFieldsSection += `Check asset facts (e.g., isSFHA, loanType, floodZone, loanAmount, collateralCode) and apply the conditions described above. `;
        evidenceFieldsSection += `If the conditions in the workflow rules are not met, adjust the evidence requirement accordingly.\n`;
      }
      const fieldDefs = evidenceDef.fieldDefinitions || [];
      evidenceFieldsSection += fieldDefs.length === 0
        ? `Fields: None (workflow-only — evaluate using workflow rules and asset facts above)\n`
        : `Fields:\n`;
      
      for (const fieldDef of fieldDefs) {
        const fieldValue = evidenceFieldsData[fieldDef.key];
        const value = fieldValue?.value;
        const isMissing = fieldValue?.missing || value === null || value === undefined || value === '';
        
        evidenceFieldsSection += `  - **${fieldDef.label}** (key: ${fieldDef.key})\n`;
        evidenceFieldsSection += `    Type: ${fieldDef.type}\n`;
        evidenceFieldsSection += `    Required: ${fieldDef.required ? 'Yes' : 'No'}\n`;
        if (fieldDef.type === 'enum' && fieldDef.enumValues && fieldDef.enumValues.length > 0) {
          evidenceFieldsSection += `    Allowed values (use exactly one): ${fieldDef.enumValues.join(', ')}\n`;
        }
        if (fieldDef.workflowRules) {
          evidenceFieldsSection += `    Workflow Rules: ${fieldDef.workflowRules}\n`;
          evidenceFieldsSection += `    IMPORTANT: Apply these workflow rules when evaluating this field. Check other field values and apply the conditions described.\n`;
        }
        if (isMissing) {
          evidenceFieldsSection += `    Value: NOT CAPTURED\n`;
          if (fieldValue?.error) {
            evidenceFieldsSection += `    Error: ${fieldValue.error}\n`;
          }
        } else {
          // Format value for display - handle boolean/string conversions
          let displayValue = value;
          if (fieldDef.type === 'boolean') {
            // Normalize boolean values for display
            if (value === true || value === 'true' || value === 'True' || value === 'yes' || value === 'Yes' || value === 'YES') {
              displayValue = 'Yes';
            } else if (value === false || value === 'false' || value === 'False' || value === 'no' || value === 'No' || value === 'NO') {
              displayValue = 'No';
            }
          }
          // For boolean fields, explicitly show the interpretation
          if (fieldDef.type === 'boolean') {
            const boolValue = displayValue === 'Yes' || displayValue === true || displayValue === 'true' || displayValue === 'True' || displayValue === 'YES';
            evidenceFieldsSection += `    Value: ${JSON.stringify(displayValue)} (interpreted as: ${boolValue ? 'TRUE' : 'FALSE'})\n`;
          } else {
            evidenceFieldsSection += `    Value: ${JSON.stringify(displayValue)}\n`;
          }
          evidenceFieldsSection += `    Confidence: ${fieldValue?.confidence || 'medium'}\n`;
          if (fieldValue?.source) {
            evidenceFieldsSection += `    Source: ${fieldValue.source}\n`;
          }
        }
      }
    }
    
    // Build additional asset facts section (relevant facts not in evidence definitions)
    let additionalFactsSection = '';
    const evidenceFieldKeys = new Set<string>();
    for (const evidenceDef of item.evidenceDefinitions || []) {
      for (const fieldDef of evidenceDef.fieldDefinitions || []) {
        evidenceFieldKeys.add(fieldDef.key);
      }
    }
    
    const additionalFacts: Record<string, any> = {};
    Object.entries(facts).forEach(([key, fact]) => {
      if (!evidenceFieldKeys.has(key) && fact && (fact as any).value !== null && (fact as any).value !== undefined) {
        additionalFacts[key] = (fact as any).value;
      }
    });
    
    if (Object.keys(additionalFacts).length > 0) {
      additionalFactsSection = `\n**Additional Asset Facts (for context):**\n`;
      Object.entries(additionalFacts).forEach(([key, value]) => {
        additionalFactsSection += `  - ${key}: ${JSON.stringify(value)}\n`;
      });
    }
    
    // Build workflow context section (for future workflow rules)
    let workflowContextSection = '';
    // For now, AI will infer workflow logic from item text and evidence
    // In next version, we'll add workflowRules field and pass it here
    
    const prompt = `You are a qualified bank officer reviewing a loan application and compliance checklist items according to USA banking standards and regulations.

**Checklist Item:**
${item.text}

**Evidence Fields (EXACT KEYS FROM EVIDENCE DEFINITIONS):**
${evidenceFieldsSection || 'No evidence definitions found.'}
${additionalFactsSection}
${workflowContextSection}

**Evaluation Instructions:**

1. **Review all evidence fields** - Check if required fields are captured and have valid values
   - For boolean fields: "Yes", "true", true, "True", "YES" all mean TRUE. "No", "false", false, "False", "NO" all mean FALSE.
   - Interpret field values according to standard banking practices and regulatory requirements.

2. **Analyze workflow logic** - For each evidence, review the workflow rules provided:
   - Check asset facts (e.g., isSFHA, loanType, floodZone, loanAmount, collateralCode) against the workflow rules
   - If workflow rules specify conditions for when evidence is required/optional, apply those rules
   - Use BOTH your banking knowledge AND the specific workflow rules provided by the user
   - If conditions in workflow rules are not met, adjust evidence requirement accordingly
   - Example: If workflow rules say "required if property is in SFHA" and isSFHA is false, mark evidence as optional
   - Determine if the item is applicable based on the evidence:
   - If a prerequisite condition is not met (e.g., property not in SFHA for flood insurance items), mark as NA
   - If required evidence is missing, mark as FAIL or NEEDS_REVIEW
   - If all requirements are met according to banking standards, mark as PASS
   - Apply standard banking logic and regulatory requirements when evaluating each item

3. **Consider field confidence** - Low confidence values may require review

4. **Provide clear reasoning** - Explain your decision based on the evidence and banking standards, specifically mentioning which fields led to your conclusion and how they relate to the regulatory requirements.

**Status Definitions:**
- **PASS**: All requirements are met according to banking standards, evidence is complete and valid
- **FAIL**: Requirements are not met, evidence is missing or invalid per banking regulations
- **NA**: Item is not applicable (e.g., prerequisite condition not met, workflow rule indicates not needed)
- **NEEDS_REVIEW**: Evidence is present but confidence is low, or there are conflicting values, or manual review is recommended

**Response Format (JSON only):**
{
  "status": "PASS" | "FAIL" | "NA" | "NEEDS_REVIEW",
  "findingText": "Brief explanation (1-2 sentences)",
  "reasoning": "Detailed explanation of your decision, including which fields were considered and why, referencing banking standards where applicable",
  "confidence": "high" | "medium" | "low"
}

Return ONLY valid JSON, no additional text.`;

    let parsed: ChecklistItemEvaluationResult;
    
    if (USE_VERTEX_AI) {
      // Use Vertex AI through proxy (enterprise security)
      console.log('[evaluateChecklistItemWithAI] ✅ Using Vertex AI (Enterprise Secure)');
      const result = await callVertexAIProxy(prompt, undefined, true);
      // Result is already parsed JSON object from generateJSONWithVertexAI
      parsed = result as ChecklistItemEvaluationResult;
      serviceUsed = 'VERTEX_AI';
    } else {
      // SECURITY: Public Gemini API is DISABLED for bank security compliance
      console.error('[evaluateChecklistItemWithAI] ⚠️ SECURITY ERROR: Public Gemini API is disabled');
      throw new Error('SECURITY VIOLATION: Public Gemini API is disabled for bank security compliance. Vertex AI must be used. Set USE_VERTEX_AI=true.');
    }
    
    // Validate parsed response
    try {
      // Ensure parsed is a valid object
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Invalid response format');
      }
    } catch (parseError) {
      console.error('Failed to parse AI evaluation response:', parseError);
      // Fallback response
      parsed = {
        status: 'NEEDS_REVIEW',
        findingText: 'AI evaluation failed - manual review required',
        confidence: 'low',
        reasoning: `Failed to parse AI response: ${parseError}`,
      };
    }
    
    // Validate status
    if (!['PASS', 'FAIL', 'NA', 'NEEDS_REVIEW'].includes(parsed.status)) {
      parsed.status = 'NEEDS_REVIEW';
    }
    
    // Validate confidence
    if (!['high', 'medium', 'low'].includes(parsed.confidence)) {
      parsed.confidence = 'medium';
    }
    
    // Security check: Ensure Vertex AI was used
    if (serviceUsed !== 'VERTEX_AI') {
      console.error('[evaluateChecklistItemWithAI] ⚠️ SECURITY WARNING: Public Gemini API was used instead of Vertex AI!');
      throw new Error('SECURITY VIOLATION: Public Gemini API cannot be used for bank data. Vertex AI must be used.');
    }
    
    console.log('[evaluateChecklistItemWithAI] ✅ Evaluation complete:', {
      serviceUsed,
      status: parsed.status,
      confidence: parsed.confidence,
    });
    
    return parsed;
  } catch (error: any) {
    console.error('Error evaluating checklist item with AI:', error);
    // Return fallback response
    return {
      status: 'NEEDS_REVIEW',
      findingText: 'AI evaluation error - manual review required',
      confidence: 'low',
      reasoning: `Error: ${error.message}`,
    };
  }
}

/**
 * Flatten facts to a simple key-value list for prompt (value from .value or first extractedValue).
 */
function flattenFactsForPrompt(facts: Record<string, any>): string {
  const lines: string[] = [];
  for (const [key, fact] of Object.entries(facts)) {
    if (fact == null) continue;
    const v = fact.value ?? fact.extractedValues?.[0]?.value;
    if (v !== null && v !== undefined && v !== '') {
      lines.push(`${key}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`);
    }
  }
  return lines.length ? lines.join('\n') : '(no facts)';
}

/**
 * Evaluate a field's workflow rule using AI when pattern-based evaluation doesn't match.
 * Uses all asset facts and the natural-language workflow rule to derive the field value.
 * @param workflowRules - Natural language rule (e.g. "if loanNumber AND borrowerName are present THEN yes ELSE no")
 * @param facts - All asset facts for the asset (key -> { value?, extractedValues? })
 * @param fieldDef - Optional field definition for type/enum constraints
 * @returns The derived value (e.g. "yes", "no") or undefined on error
 */
export async function evaluateFieldWorkflowRuleWithAI(
  workflowRules: string,
  facts: Record<string, any>,
  fieldDef?: { key?: string; type?: string; enumValues?: string[] }
): Promise<string | number | boolean | null | undefined> {
  if (!USE_VERTEX_AI) return undefined;
  if (!workflowRules || typeof workflowRules !== 'string' || workflowRules.trim() === '') return undefined;

  const factsText = flattenFactsForPrompt(facts);
  const typeHint = fieldDef?.type === 'boolean' ? ' Return exactly "yes" or "no" for boolean.' : '';
  const enumHint = fieldDef?.enumValues && fieldDef.enumValues.length > 0
    ? ` Allowed values (return exactly one): ${fieldDef.enumValues.join(', ')}.`
    : '';

  const fieldKey = fieldDef?.key ?? '';
  const collateralCodeVal = facts.collateralCode != null ? (facts.collateralCode?.value ?? facts.collateralCode?.extractedValues?.[0]?.value ?? (typeof facts.collateralCode === 'object' ? undefined : facts.collateralCode)) : undefined;
  console.log('[evaluateFieldWorkflowRuleWithAI]', {
    field: fieldKey,
    rulePreview: workflowRules.trim().slice(0, 80),
    factKeys: Object.keys(facts),
    collateralCodeInFacts: 'collateralCode' in facts,
    collateralCodeValue: collateralCodeVal,
    factsTextLength: factsText.length,
  });

  const prompt = `You are a rule evaluator. Given asset facts and a workflow rule, output the value for the field.

**Workflow rule:**
${workflowRules.trim()}

**Asset facts (key: value):**
${factsText}

**Instructions:**
- Apply the rule to the facts above and determine the field value.
- For equality checks (e.g. "field = 43" or "field = 63"), treat the fact value as matching if it equals that number in either numeric or string form: 43 matches 43 or "43".
- For "if condition then X else Y": first evaluate the condition; if true, output X; otherwise output Y. For "A or B or C" in the condition, true means any of A, B, or C is true.
- Return only the value (e.g. yes, no, or the computed value). No explanation.${typeHint}${enumHint}
- If the rule cannot be applied or facts are missing, return null.

Return ONLY valid JSON in this exact format:
{"value": "the value or null"}`;

  try {
    const result = await callVertexAIProxy(prompt, undefined, true);
    if (result == null || typeof result !== 'object') {
      console.log('[evaluateFieldWorkflowRuleWithAI]', fieldKey, 'raw result invalid:', result);
      return undefined;
    }
    const value = (result as { value?: string | number | boolean | null }).value;
    console.log('[evaluateFieldWorkflowRuleWithAI]', fieldKey, 'parsed value:', value);
    if (value === undefined) return undefined;
    if (value === null) return null;
    if (fieldDef?.type === 'boolean' && typeof value === 'string') {
      const lower = value.trim().toLowerCase();
      if (lower === 'yes' || lower === 'true' || lower === '1') return 'yes';
      if (lower === 'no' || lower === 'false' || lower === '0') return 'no';
    }
    return value as string | number | boolean;
  } catch (err) {
    console.warn('[evaluateFieldWorkflowRuleWithAI] Error:', err);
    return undefined;
  }
}

